// Updated file: LandingPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiService from '../../services/ApiServices';
import {
    FaClock,
    FaGlobe,
    FaTools,
    FaInfoCircle,
    FaCalendarAlt,
    FaUser,
    FaRegCalendarCheck,
    FaChevronRight,
    FaMapMarkerAlt,
    FaVideo,
    FaPhone,
    FaLaptop
} from "react-icons/fa";
import './calendarStyles.css';

const LandingPage = () => {
    const { schema, usersslug } = useParams();
    const [events, setEvents] = useState([]);
    const [userInfo, setUserInfo] = useState({ name: 'User' });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [logoUrl, setLogoUrl] = useState(null);
    const [backgroundUrl, setBackgroundUrl] = useState(null);

    useEffect(() => {
        const fetchLandingData = async () => {
            try {
                setLoading(true);
                const response = await apiService.get(`/api/booking-page/user-events/${schema}/${usersslug}`);
                console.log('API Response:', response);
                let eventvalue = response.events

                if (eventvalue.events && Array.isArray(eventvalue.events)) {
                    const sortedEvents = eventvalue.events
                        .filter(event => !event.isprivate)
                        .sort((a, b) => (a.order_by ?? 0) - (b.order_by ?? 0));

                    setEvents(sortedEvents);
                } else {
                    setEvents([]);
                }

                setUserInfo(eventvalue.user || { name: 'User' });

                if (response.landingpage_url) {
                    setBackgroundUrl(response.landingpage_url);
                }
                if (response.logo_url) {
                    setLogoUrl(response.logo_url);
                }
            } catch (e) {
                console.error('Error fetching data:', e);
                setError(e.message);
            } finally {
                setLoading(false);
            }
        };

        if (schema && usersslug) {
            fetchLandingData();
        }
    }, [schema, usersslug]);

    const getUserInitial = () => {
        return userInfo.name.charAt(0).toUpperCase();
    };

    const getUserName = () => {
        return userInfo.name || 'User';
    };

    // Generate slug from title for URL
    const generateEventSlug = (event) => {
        if (event.slug) return event.slug;
        if (event.link) return event.link.split('/').pop();
        return event.title
            ? `${event.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}-${Math.random().toString(36).substr(2, 4)}`
            : `event-${Math.random().toString(36).substr(2, 6)}`;
    };

    // Get appropriate icon based on event type
    const getEventIcon = (event) => {
        if (event.event_image) {
            return <img className="text-info"
                src={event.event_image}
                alt={`${getUserName()} logo`}
                style={{
                    maxWidth: '50px',
                    maxHeight: '50px',
                    objectFit: 'contain',
                    borderRadius: '50px',
                }}
            />
        } else {
            return <FaLaptop className="text-info" />;
        }

        // const type = eventType?.toLowerCase();



    };

    if (loading) {
        return (
            <div className="container my-5">
                <div className="row justify-content-center">
                    <div className="col-md-6 text-center">
                        <div className="card shadow border-0">
                            <div className="card-body py-5">
                                <div className="spinner-border text-primary mb-3" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <h5 className="text-dark mb-2">Loading Scheduling Page</h5>
                                <p className="text-muted">Please wait while we prepare your booking options</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container my-5">
                <div className="alert alert-danger shadow border-0" role="alert">
                    <h5 className="alert-heading">
                        <FaInfoCircle className="me-2" />
                        Unable to Load Scheduling Page
                    </h5>
                    <p className="mb-0">Error fetching data: {error}</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-vh-100 bg-light"
            style={backgroundUrl ? {
                backgroundImage: `url(${backgroundUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed'
            } : {}}
        >
            {/* Main Container */}
            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-lg-10 col-xl-9">

                        {/* Header Card - Organizer Info */}
                        <div className="card shadow-sm border-0 mb-4">
                            <div className="card-body text-center p-5">
                                {/* Organizer Avatar / Logo */}
                                <div className="d-flex justify-content-center mb-4">

                                    {logoUrl ? (
                                        <img
                                            src={logoUrl}
                                            alt={`${getUserName()} logo`}
                                            style={{
                                                maxWidth: '150px',
                                                maxHeight: '100px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    ) : (
                                        <div
                                            className="bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                            style={{ width: '100px', height: '100px' }}
                                        >
                                            <span className="text-white fw-bold fs-3">
                                                {getUserInitial()}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Welcome Text */}
                                <h6 className="text-muted mb-2">
                                    <FaRegCalendarCheck className="me-2" />
                                    Schedule a meeting with
                                </h6>
                                <h2 className="fw-bold text-dark mb-4">{getUserName()}</h2>

                                {/* Smaller welcome message */}
                                <p className="text-muted mb-0" style={{ fontSize: '0.95rem' }}>
                                    Welcome to my scheduling page. Please follow the instructions to add an event to my calendar.
                                </p>
                            </div>
                            <div className="card-body p-5">
                                {events.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FaCalendarAlt size={64} className="text-muted mb-3 opacity-50" />
                                        <h5 className="text-dark mb-2">No Available Meetings</h5>
                                        <p className="text-muted mb-0">
                                            No scheduling options are currently available. Please check back later.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="row g-4">
                                        {events.map((event, index) => (
                                            <div key={event.id || index} className="col-12 col-md-6">
                                                <Link
                                                    to={`/booking/${schema}/${usersslug}/${generateEventSlug(event)}`}
                                                    className="text-decoration-none"
                                                >
                                                    <div className="card border-0 h-100 shadow-sm hover-shadow transition-all">
                                                        <div className="card-body p-4">
                                                            <div className="d-flex align-items-start">
                                                                {/* Event Icon */}
                                                                <div className="flex-shrink-0 me-3">
                                                                    <div
                                                                        className="bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                                                        style={{ width: '50px', height: '50px' }}
                                                                    >

                                                                        {getEventIcon(event)}
                                                                    </div>
                                                                </div>

                                                                {/* Event Details */}
                                                                <div className="flex-grow-1">
                                                                    <h6 className="fw-bold text-dark mb-2">
                                                                        {event.title || 'Meeting'}
                                                                    </h6>



                                                                    {event.locationtype && (
                                                                        <div className="d-flex align-items-center text-muted mb-2">
                                                                            <FaMapMarkerAlt className="me-2" size={12} />
                                                                            <small className="text-capitalize">
                                                                                {event.locationtype}
                                                                            </small>
                                                                        </div>
                                                                    )}

                                                                    {event.description && (
                                                                        <p className="text-muted small mb-0">
                                                                            {event.description.length > 80
                                                                                ? `${event.description.substring(0, 80)}...`
                                                                                : event.description
                                                                            }
                                                                        </p>
                                                                    )}
                                                                </div>

                                                                {/* Arrow */}
                                                                <div className="flex-shrink-0">
                                                                    <FaChevronRight className="text-primary mt-1" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Link>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LandingPage;